<template>
    <div class="wrapper">
        <div class="main visual-2 doc-profile-main">
            <Header />

            <div class="profile-doctor">
                <div class="doctor-container page-content">

                    <div class="wizard-card">

                        <div class="page-header mb-4">
                            <h3 class="page-title"><i class="fa-solid fa-users"></i> User Management</h3>
                            <p class="text-muted">Manage system users and their roles</p>
                        </div>

                        <!-- Action Bar -->
                        <div class="action-bar mb-4" v-if="canManageUsers">
                            <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                <div class="d-flex align-items-center gap-3">
                                    <button class="btn-theme-primary" @click="openCreateModal">
                                        <i class="fa-solid fa-plus me-2"></i>
                                        Create New User
                                    </button>
                                    <div v-if="canManageUsers" class="text-muted small">
                                        <i class="fa-solid fa-info-circle me-1"></i>
                                        Click to add a new system user
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Stats -->
                        <div class="stats-section mb-4">
                            <div class="row g-3">
                                <div class="col-md-3">
                                    <div class="stat-card">
                                        <div class="stat-icon bg-primary"><i class="fa-solid fa-users"></i></div>
                                        <div class="stat-content">
                                            <h5>{{ totalUsers }}</h5>
                                            <p>Total Users</p>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="stat-card">
                                        <div class="stat-icon bg-success"><i class="fa-solid fa-toggle-on"></i></div>
                                        <div class="stat-content">
                                            <h5>{{ activeUsers }}</h5>
                                            <p>Active Users</p>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="stat-card">
                                        <div class="stat-icon bg-danger"><i class="fa-solid fa-toggle-off"></i></div>
                                        <div class="stat-content">
                                            <h5>{{ inactiveUsers }}</h5>
                                            <p>Inactive Users</p>
                                        </div>
                                    </div>
                                </div>
                                <!-- <div class="col-md-3">
                                    <div class="stat-card">
                                        <div class="stat-icon bg-info"><i class="fa-solid fa-user-shield"></i></div>
                                        <div class="stat-content">
                                            <h5>{{ adminUsers }}</h5>
                                            <p>Admins</p>
                                        </div>
                                    </div>
                                </div> -->
                            </div>
                        </div>

                        <!-- Table Panel -->
                        <div class="glass-panel">
                            <div class="table-header">
                                <h4 class="panel-title"><i class="fa-solid fa-list me-2"></i> Users List</h4>
                                <div class="table-filters">
                                    <div class="row g-2">
                                        <div class="col-auto">
                                            <div class="search-box table-search-box">
                                                <i class="fa-solid fa-search search-icon"></i>
                                                <input v-model="searchQuery" type="text" class="search-input"
                                                    placeholder="Search users..." />
                                            </div>
                                        </div>
                                        <div v-if="canManageUsers" class="col-auto">
                                            <select v-model="selectedHospitalId" class="form-select-sm"
                                                :disabled="isLoadingHospitals">
                                                <option value="">All Hospitals</option>
                                                <option v-for="hospital in hospitals" :key="hospital.id || hospital._id"
                                                    :value="hospital.id || hospital._id">
                                                    {{ hospital.name }}
                                                </option>
                                            </select>
                                        </div>
                                        <div class="col-auto">
                                            <select v-model="statusFilter" class="form-select-sm">
                                                <option value="">All Status</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                        <div class="col-auto">
                                            <select v-model="roleFilter" class="form-select-sm">
                                                <option value="">All Roles</option>
                                                <!-- <option value="admin">Admin</option> -->
                                                <option value="super-admin">Super Admin</option>
                                                <option value="hospital">Hospital</option>
                                                <option value="doctor">Doctor</option>
                                                <option value="patient">Patient</option>
                                                <!-- <option value="staff">Staff</option> -->
                                            </select>
                                        </div>
                                        <div class="col-auto">
                                            <select v-model="sortBy" class="form-select-sm">
                                                <option value="name">Sort by Name</option>
                                                <option value="email">Sort by Email</option>
                                                <option value="newest">Sort by Newest</option>
                                            </select>
                                        </div>
                                        <div class="col-auto">
                                            <select v-model="itemsPerPage" class="form-select-sm">
                                                <option value="10">10 per page</option>
                                                <option value="25">25 per page</option>
                                                <option value="50">50 per page</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Loading -->
                            <div v-if="isLoading" class="text-center py-5">
                                <div class="spinner-border text-primary" role="status"></div>
                                <p class="mt-3 text-muted">Loading users...</p>
                            </div>

                            <!-- Empty -->
                            <div v-else-if="filteredUsers.length === 0" class="empty-state">
                                <div class="empty-icon"><i class="fa-solid fa-users-slash"></i></div>
                                <h5>No users found</h5>
                                <p v-if="canManageUsers" class="text-muted">Click "Create New User" to add the first user</p>
                                <p v-else class="text-muted">No users are available to view.</p>
                                <button v-if="canManageUsers" class="btn-theme-primary mt-3" @click="openCreateModal">
                                    <i class="fa-solid fa-plus me-2"></i> Create Your First User
                                </button>
                            </div>

                            <!-- Table -->
                            <div v-else class="table-responsive">
                                <table class="table-theme">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>Hospital</th>
                                            <th>Role</th>
                                            <th>Status</th>
                                            <th v-if="canManageUsers">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="user in filteredUsers" :key="user.id || user._id">
                                            <td>
                                                <div class="user-name-cell">
                                                    <div class="user-avatar">
                                                        {{ getInitials(user) }}
                                                    </div>
                                                    <strong>{{ getUserFullName(user) }}</strong>
                                                </div>
                                            </td>
                                            <td>
                                                <a :href="`mailto:${user.email}`" class="email-link">{{ user.email
                                                    }}</a>
                                            </td>
                                            <td>{{ user.phoneNumber || '—' }}</td>
                                            <td>{{ getUserHospitalName(user) }}</td>
                                            <td>
                                                <span class="role-badge" :class="getUserRole(user)">
                                                    {{ capitalize(getUserRole(user)) }}
                                                </span>
                                            </td>
                                            <td>
                                                <span class="status-badge" :class="user.active ? 'active' : 'inactive'">
                                                    <i class="fa-solid fa-circle me-2"></i>
                                                    {{ user.active ? 'Active' : 'Inactive' }}
                                                </span>
                                            </td>
                                            <td v-if="canManageUsers">
                                                <div class="table-actions">
                                                    <button class="btn-action" @click="editUser(user)" title="Edit">
                                                        <i class="fa-solid fa-edit"></i>
                                                    </button>
                                                    <button class="btn-action text-danger" @click="deleteUser(user)"
                                                        title="Delete">
                                                        <i class="fa-solid fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- Pagination -->
                            <div v-if="users.length > 0" class="table-footer">
                                <div class="row align-items-center">
                                    <div class="col-md-6">
                                        <p class="mb-0 text-muted">
                                            Showing {{ paginationStart }}-{{ paginationEnd }} of {{ totalUserCount }} users
                                        </p>
                                    </div>
                                    <div class="col-md-6">
                                        <nav>
                                            <ul class="pagination justify-content-end mb-0">
                                                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                                    <button class="page-link" @click="prevPage">
                                                        <i class="fa-solid fa-chevron-left"></i>
                                                    </button>
                                                </li>
                                                <li v-if="visiblePages.length && visiblePages[0] > 1" class="page-item">
                                                    <button class="page-link" @click="currentPage = 1">1</button>
                                                </li>
                                                <li v-if="visiblePages.length && visiblePages[0] > 2"
                                                    class="page-item disabled">
                                                    <span class="page-link">&hellip;</span>
                                                </li>
                                                <li v-for="page in visiblePages" :key="page" class="page-item"
                                                    :class="{ active: page === currentPage }">
                                                    <button class="page-link" @click="currentPage = page">{{ page
                                                        }}</button>
                                                </li>
                                                <li v-if="visiblePages.length && visiblePages[visiblePages.length - 1] < totalPages - 1"
                                                    class="page-item disabled">
                                                    <span class="page-link">&hellip;</span>
                                                </li>
                                                <li v-if="visiblePages.length && visiblePages[visiblePages.length - 1] < totalPages"
                                                    class="page-item">
                                                    <button class="page-link" @click="currentPage = totalPages">{{
                                                        totalPages }}</button>
                                                </li>
                                                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                                    <button class="page-link" @click="nextPage">
                                                        <i class="fa-solid fa-chevron-right"></i>
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <FooterView />
        </div>

        <!-- User Modal -->
        <UserManagementModal :show="showModal" :user="selectedUser" @close="closeModal" @saved="handleUserSaved"
            @deleted="handleUserDeleted" />
    </div>
</template>

<script>
import Header from "@/layout/Header.vue";
import FooterView from "@/layout/Footer.vue";
import UserManagementModal from "@/components/users/UserManagementModal.vue";
import { UsersService } from "@/services/users.service";
import { AuthService } from "@/services/auth.service";

export default {
    name: "UserManagement",
    components: { Header, FooterView, UserManagementModal },

    data() {
        return {
            users: [],
            isLoading: false,
            searchQuery: "",
            statusFilter: "",
            roleFilter: "",
            sortBy: "name",
            currentPage: 1,
            itemsPerPage: 10,
            totalUserCount: 0,
            activeUserCount: 0,
            inactiveUserCount: 0,
            showModal: false,
            selectedUser: null,
            hospitals: [],
            selectedHospitalId: "",
            isLoadingHospitals: false,
            searchDebounceTimer: null
        };
    },

    computed: {
        filteredUsers() {
            return this.users || [];
        },

        totalPages() {
            return Math.max(1, Math.ceil(this.totalUserCount / Number(this.itemsPerPage)));
        },

        visiblePages() {
            const total = this.totalPages;
            const maxVisible = 7;
            if (total <= maxVisible) return Array.from({ length: total }, (_, i) => i + 1);

            const half = Math.floor(maxVisible / 2);
            let start = Math.max(1, this.currentPage - half);
            let end = Math.min(total, this.currentPage + half);
            if (start === 1) end = Math.min(total, maxVisible);
            if (end === total) start = Math.max(1, total - maxVisible + 1);

            const pages = [];
            for (let i = start; i <= end; i++) pages.push(i);
            return pages;
        },

        totalUsers() { return this.totalUserCount; },
        activeUsers() { return this.activeUserCount; },
        inactiveUsers() { return this.inactiveUserCount; },
        adminUsers() { return this.users.filter(u => this.getUserRole(u) === "admin").length; }
        ,
        paginationStart() {
            if (!this.totalUserCount || !this.users.length) return 0;
            return (this.currentPage - 1) * Number(this.itemsPerPage) + 1;
        },
        paginationEnd() {
            if (!this.totalUserCount || !this.users.length) return 0;
            return Math.min(this.paginationStart + this.users.length - 1, this.totalUserCount);
        },
        currentUserRole() {
            return this.$store?.state?.user?.currentUser?.objectType || "";
        },
        canManageUsers() {
            return ["superadmin", "super_admin", "super-admin"].includes(this.currentUserRole);
        }
    },

    watch: {
        totalPages(val) {
            if (this.currentPage > val) this.currentPage = val;
            if (this.currentPage < 1) this.currentPage = 1;
        },
        searchQuery() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                if (this.currentPage !== 1) {
                    this.currentPage = 1;
                } else {
                    this.fetchUsers();
                }
            }, 400);
        },
        statusFilter() {
            this.resetToFirstPageAndFetch();
        },
        roleFilter() {
            this.resetToFirstPageAndFetch();
        },
        sortBy() {
            this.resetToFirstPageAndFetch();
        },
        itemsPerPage() {
            this.resetToFirstPageAndFetch();
        },
        currentPage() {
            this.fetchUsers();
        },
        selectedHospitalId() {
            this.resetToFirstPageAndFetch();
        }
    },

    mounted() {
        if (this.canManageUsers) this.fetchHospitals();
        this.fetchUsers();
    },

    methods: {
        async fetchHospitals() {
            if (this.hospitals.length || this.isLoadingHospitals) return;

            this.isLoadingHospitals = true;
            try {
                const response = await AuthService.getSuperAdminHospitals();
                this.hospitals = response.data?.items || response.data || [];
            } catch (error) {
                console.error("Error fetching hospitals:", error);
                this.$store.commit("toast/NEW", {
                    type: "error",
                    message: "Failed to load hospitals list"
                });
            } finally {
                this.isLoadingHospitals = false;
            }
        },

        async fetchUsers() {
            this.isLoading = true;
            try {
                const limit = Number(this.itemsPerPage);
                const params = {
                    page: this.currentPage,
                    limit,
                    sort: this.sortBy
                };
                if (this.canManageUsers && this.selectedHospitalId) {
                    params.hospitalId = this.selectedHospitalId;
                }
                if (this.searchQuery.trim()) {
                    params.search = this.searchQuery.trim();
                }
                if (this.roleFilter) {
                    params.role = this.roleFilter;
                }
                if (this.statusFilter) {
                    params.status = this.statusFilter;
                }

                const response = await UsersService.getAll(params);
                const data = response.data;

                if (data.status === 200 || data.statusCode === "200" || data.success === true) {
                    const payload = data.data?.data || data.data || {};
                    this.users = payload.items || payload.data || [];
                    this.totalUserCount = this.getCountValue(
                        payload,
                        ["total", "totalCount", "count", "totalItems"],
                        payload.meta?.total ?? this.users.length
                    );
                    this.activeUserCount = this.getCountValue(
                        payload,
                        ["activeCount", "activeUserCount", "activeUsers"],
                        this.users.filter(user => user.active).length
                    );
                    this.inactiveUserCount = this.getCountValue(
                        payload,
                        ["inactiveCount", "inactiveUserCount", "inactiveUsers"],
                        this.users.filter(user => !user.active).length
                    );
                } else {
                    this.$store.commit("toast/NEW", { type: "error", message: data.message || "Failed to fetch users" });
                }
            } catch (error) {
                console.error("Error fetching users:", error);
                this.$store.commit("toast/NEW", { type: "error", message: "An error occurred while fetching users" });
            } finally {
                this.isLoading = false;
            }
        },

        getCountValue(payload, keys, fallback) {
            for (const key of keys) {
                if (payload?.[key] !== undefined && payload?.[key] !== null) {
                    return Number(payload[key]);
                }
            }

            return Number(fallback);
        },

        resetToFirstPageAndFetch() {
            if (this.currentPage !== 1) {
                this.currentPage = 1;
                return;
            }

            this.fetchUsers();
        },

        openCreateModal() {
            this.selectedUser = null;
            this.showModal = true;
        },

        editUser(user) {
            this.selectedUser = { ...user };
            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
            this.selectedUser = null;
        },

        handleUserSaved() {
            this.fetchUsers();
        },

        handleUserDeleted() {
            this.fetchUsers();
        },

        async deleteUser(user) {
            const name = this.getUserFullName(user);
            if (!confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) return;

            try {
                const userId = user._id || user.id;
                const response = await UsersService.delete(userId);

                if (response.data.status === 200) {
                    this.$store.commit("toast/NEW", {
                        type: "success",
                        message: response?.data?.data?.message || "User deleted successfully!"
                    });
                    this.fetchUsers();
                } else {
                    this.$store.commit("toast/NEW", { type: "error", message: response?.data?.message || "Failed to delete user" });
                }
            } catch (error) {
                const errMsg = error?.response?.data;
                this.$store.commit("toast/NEW", {
                    type: "error",
                    message: errMsg?.data?.message || errMsg?.message || "An error occurred while deleting user"
                });
            }
        },

        getInitials(user) {
            const first = this.getUserFirstName(user)?.[0] || "";
            const last = this.getUserLastName(user)?.[0] || "";
            return `${first}${last}`.toUpperCase() || "U";
        },

        getUserFirstName(user) {
            return user?.firstname || user?.firstName || "";
        },

        getUserLastName(user) {
            return user?.lastname || user?.lastName || "";
        },

        getUserFullName(user) {
            const name = `${this.getUserFirstName(user)} ${this.getUserLastName(user)}`.trim();
            return this.capitalizeWords(name) || "Unnamed User";
        },

        getUserRole(user) {
            return user?.objectType || user?.role || "";
        },

        getUserHospitalName(user) {
            return user?.hospital?.name ||
                user?.activeHospital?.name ||
                user?.hospitalName ||
                user?.hospital?.title ||
                "—";
        },

        capitalizeWords(value) {
            if (!value) return "";
            return value
                .split(" ")
                .filter(Boolean)
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(" ");
        },

        capitalize(str) {
            if (!str) return "";
            return str.charAt(0).toUpperCase() + str.slice(1);
        },

        formatDate(dateString) {
            if (!dateString) return "N/A";
            return new Date(dateString).toLocaleDateString("en-US", {
                year: "numeric", month: "short", day: "numeric"
            });
        },

        prevPage() { if (this.currentPage > 1) this.currentPage--; },
        nextPage() { if (this.currentPage < this.totalPages) this.currentPage++; }
    }
};
</script>

<style scoped>
.page-title {
    font-size: 24px;
    font-weight: 600;
    color: #0144A6;
}

.wrapper,
.main,
.profile-doctor {
    height: 100vh;
}

.doctor-container {
    height: 50vh;
}

.page-content {
    width: 100%;
    margin: 150px auto;
    padding-bottom: 50px;
}

.wizard-card {
    width: 100%;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
    padding: 30px;
    height: calc(100vh - 160px);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
}

/* Action Bar */
.action-bar {
    background: #f8faff;
    padding: 20px;
    border-radius: 12px;
    border: 1px solid #eef2f6;
}

.btn-theme-primary {
    background: #3c78f9;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
}

.btn-theme-primary:hover {
    background: #2a65e0;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(60, 120, 249, 0.3);
}

/* Search */
.search-box {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 12px;
    color: #aaa;
    font-size: 14px;
}

.search-input {
    padding: 9px 14px 9px 36px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    min-width: 280px;
    transition: all 0.2s;
}

.search-input:focus {
    outline: none;
    border-color: #3c78f9;
    box-shadow: 0 0 0 3px rgba(60, 120, 249, 0.1);
}

.table-search-box .search-input {
    height: 34px;
    min-width: 220px;
    padding-bottom: 6px;
    padding-top: 6px;
}

/* Stats */
.stats-section {
    margin-bottom: 30px;
}

.stat-card {
    background: white;
    border: 1px solid #eef2f6;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 15px;
    transition: all 0.3s ease;
    height: 100%;
}

.stat-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.stat-icon {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
}

.stat-icon.bg-primary {
    background: #3c78f9;
}

.stat-icon.bg-success {
    background: #28a745;
}

.stat-icon.bg-danger {
    background: #dc3545;
}

.stat-icon.bg-info {
    background: #17a2b8;
}

.stat-content h5 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #0144A6;
}

.stat-content p {
    margin: 5px 0 0 0;
    color: #666;
    font-size: 13px;
}

/* Glass Panel */
.glass-panel {
    background: #ffffff;
    border: 1px solid #eee;
    border-radius: 20px;
    padding: 30px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    margin-bottom: 30px;
}

.panel-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #0144A6;
    margin-bottom: 0;
    display: flex;
    align-items: center;
}

.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #eef2f6;
    flex-wrap: wrap;
    gap: 12px;
}

.table-filters .form-select-sm {
    padding: 6px 12px;
    font-size: 14px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: white;
    min-width: 130px;
}

/* Table */
.table-theme {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.table-theme thead tr th {
    background: #f8faff;
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #eef2f6;
}

.table-theme tbody tr {
    transition: background 0.15s;
}

.table-theme tbody tr:hover {
    background: #f8faff;
}

.table-theme tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid #f0f2f5;
    font-size: 14px;
    color: #333;
    vertical-align: middle;
}

/* User name cell */
.user-name-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}

.user-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3c78f9, #0144A6);
    color: white;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.email-link {
    color: #3c78f9;
    text-decoration: none;
    font-size: 13px;
}

.email-link:hover {
    text-decoration: underline;
}

/* Role Badge */
.role-badge {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    display: inline-block;
}

.role-badge.admin {
    background: #fce8ff;
    color: #6a00b8;
}

.role-badge.hospital {
    background: #e8f0ff;
    color: #0144A6;
}

.role-badge.doctor {
    background: #e8fff2;
    color: #0d6e42;
}

.role-badge.patient {
    background: #fff8e8;
    color: #8a5a00;
}

.role-badge.staff {
    background: #f0f0ff;
    color: #444;
}

/* Status Badge */
.status-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
}

.status-badge.active {
    background: #e6fff2;
    color: #0d6e42;
}

.status-badge.inactive {
    background: #f8f9fa;
    color: #666;
}

.date-display {
    color: #666;
    font-size: 14px;
    display: flex;
    align-items: center;
}

/* Table Actions */
.table-actions {
    display: flex;
    gap: 8px;
}

.btn-action {
    width: 34px;
    height: 34px;
    border: 1px solid #eee;
    border-radius: 8px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    color: #555;
    font-size: 14px;
}

.btn-action:hover {
    background: #f0f5ff;
    border-color: #3c78f9;
    color: #3c78f9;
}

.btn-action.text-danger:hover {
    background: #fff0f0;
    border-color: #dc3545;
    color: #dc3545;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #666;
}

.empty-icon {
    font-size: 4rem;
    color: #ddd;
    margin-bottom: 20px;
}

/* Footer / Pagination */
.table-footer {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #eef2f6;
}

.pagination {
    display: flex;
    list-style: none;
    padding: 0;
    margin: 0;
    gap: 4px;
}

.page-item.disabled .page-link {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.page-item.active .page-link {
    background: #3c78f9;
    color: white;
    border-color: #3c78f9;
}

.page-link {
    padding: 6px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: white;
    color: #333;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
}

.page-link:hover:not(.page-item.disabled .page-link) {
    background: #f0f5ff;
    border-color: #3c78f9;
}
</style>
